import React from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { tokens } from "../../../theme/tokens";

const pulse = keyframes({
  "0%": { opacity: 1 },
  "50%": { opacity: 0.45 },
  "100%": { opacity: 1 },
});

type SkeletonVariant = "text" | "rectangular" | "circular";

interface LoadingSkeletonProps {
  variant?: SkeletonVariant;
  width?: number | string;
  height?: number | string;
  "aria-label"?: string;
}

export const LoadingSkeleton = React.forwardRef<HTMLDivElement, LoadingSkeletonProps>(
  ({ variant = "rectangular", width, height, ...props }, ref) => {
    const radius =
      variant === "circular" ? tokens.radius.full : tokens.radius.sm;
    const resolvedWidth = width ?? (variant === "circular" ? tokens.icon.lg : "100%");
    const resolvedHeight =
      height ?? (variant === "text" ? "1.2em" : variant === "circular" ? tokens.icon.lg : "100%");
    return (
      <Box
        ref={ref}
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label={props["aria-label"] ?? "Loading"}
        sx={{
          display: "block",
          width: resolvedWidth,
          height: resolvedHeight,
          backgroundColor: tokens.color.feedback.skeleton,
          borderRadius: radius,
          animation: `${pulse} 1.6s ${tokens.motion.easing} infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
    );
  }
);
LoadingSkeleton.displayName = "LoadingSkeleton";

interface EmptyStateProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ title, description, icon, action }, ref) => (
    <Box
      ref={ref}
      role="status"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: tokens.space[2],
        padding: tokens.space[5],
      }}
    >
      {icon != null && (
        <Box
          aria-hidden="true"
          sx={{
            display: "flex",
            color: tokens.color.text.secondary,
            "& svg": { fontSize: tokens.icon.lg },
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        sx={{
          fontSize: tokens.type.denseStrong.size,
          fontWeight: tokens.type.denseStrong.weight,
          lineHeight: tokens.type.denseStrong.lineHeight,
          color: tokens.color.text.primary,
          textTransform: "none",
        }}
      >
        {title}
      </Typography>
      {description != null && (
        <Typography
          sx={{
            fontSize: tokens.type.dense.size,
            lineHeight: tokens.type.dense.lineHeight,
            color: tokens.color.text.secondary,
            maxWidth: "40ch",
          }}
        >
          {description}
        </Typography>
      )}
      {action != null && <Box sx={{ marginTop: tokens.space[2] }}>{action}</Box>}
    </Box>
  )
);
EmptyState.displayName = "EmptyState";

interface ErrorStateProps {
  title?: React.ReactNode;
  message?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export const ErrorState = React.forwardRef<HTMLDivElement, ErrorStateProps>(
  ({ title, message, icon, action }, ref) => (
    <Box
      ref={ref}
      role="alert"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: tokens.space[2],
        padding: tokens.space[5],
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          display: "flex",
          color: tokens.color.status.error,
          "& svg": { fontSize: tokens.icon.lg },
        }}
      >
        {icon ?? <ErrorOutlineIcon />}
      </Box>
      <Typography
        sx={{
          fontSize: tokens.type.denseStrong.size,
          fontWeight: tokens.type.denseStrong.weight,
          lineHeight: tokens.type.denseStrong.lineHeight,
          color: tokens.color.text.primary,
          textTransform: "none",
        }}
      >
        {title ?? "Something went wrong"}
      </Typography>
      <Typography
        sx={{
          fontSize: tokens.type.dense.size,
          lineHeight: tokens.type.dense.lineHeight,
          color: tokens.color.text.secondary,
          maxWidth: "40ch",
          textTransform: "none",
        }}
      >
        {message ?? "Please try again. If the problem continues, contact support."}
      </Typography>
      {action != null && <Box sx={{ marginTop: tokens.space[2] }}>{action}</Box>}
    </Box>
  )
);
ErrorState.displayName = "ErrorState";
